<template>
  <Teleport to="body">
    <div class="toast-container">
      <Toast
        v-for="toast in toasts"
        :key="toast.id"
        :title="toast.title"
        :message="toast.message"
        :type="toast.type"
        :duration="toast.duration"
        :closable="toast.closable"
        @close="removeToast(toast.id)"
      />
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import Toast from './Toast.vue';

export interface ToastOptions {
  id?: string;
  title?: string;
  message: string;
  type?: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  closable?: boolean;
}

interface ToastItem extends Required<Omit<ToastOptions, 'id'>> {
  id: string;
}

const toasts = ref<ToastItem[]>([]);

const addToast = (options: ToastOptions) => {
  const id = options.id || `toast-${Date.now()}-${Math.random()}`;
  
  const toast: ToastItem = {
    id,
    title: options.title || '',
    message: options.message,
    type: options.type || 'info',
    duration: options.duration ?? 3000,
    closable: options.closable ?? true,
  };
  
  toasts.value.push(toast);
};

const removeToast = (id: string) => {
  const index = toasts.value.findIndex((t) => t.id === id);
  if (index > -1) {
    toasts.value.splice(index, 1);
  }
};

defineExpose({
  addToast,
  removeToast,
});
</script>

<style scoped>
.toast-container {
  position: fixed;
  top: var(--spacing-4);
  right: var(--spacing-4);
  z-index: var(--z-index-tooltip);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-3);
  pointer-events: none;
}

.toast-container > * {
  pointer-events: auto;
}

@media (max-width: 640px) {
  .toast-container {
    top: var(--spacing-2);
    right: var(--spacing-2);
    left: var(--spacing-2);
  }
}
</style>

